import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { CompanyDetail } from "../../lib/crm-types";
import { Button } from "../ui/Button";
import { cn } from "../ui/cn";
import { CompanyPicker } from "./CompanyPicker";

/**
 * The group at a glance, shown on a company that has branches.
 *
 * A group record is nearly always quiet in its own right -- CEF-the-group takes
 * no calls and raises no tickets, its nine hundred depots do -- so without this
 * the national account manager opens the most important account on the desk and
 * finds an empty page.
 *
 * Owners are broken out because "different reps manage different regions" is
 * the actual shape of the relationship, and "who has the North West?" is the
 * question the page gets asked.
 *
 * It adds branches as well as listing them, which it did not used to. The only
 * way into a group was the duplicate queue, which meant a branch could be
 * filed only if the detector had happened to flag it -- so a depot with its own
 * domain, or one somebody had already dismissed, could not be put under its
 * group at all. Adding one from the group you are looking at is the way round
 * anybody reaches for first.
 *
 * Which is also why it does not hide itself entirely on a company with no
 * branches: that company is how every group starts, and a group that could
 * only be born in the duplicate queue is one nobody can build on purpose. It
 * shows as a single line there -- a heading and a button, no stats and no
 * empty list -- because a plain account is not a group and should not be
 * dressed as one.
 *
 * A branch never shows it. The hierarchy is two levels, so a company that has
 * a parent cannot have children, and offering the button would be offering an
 * error message.
 */
export function GroupPanel({ company }: { company: CompanyDetail }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const rollup = useQuery({
    queryKey: queryKeys.crmRollup(company.id),
    queryFn: () => crmApi.rollup(company.id),
    // Only a group has one; the endpoint returns null for everything else.
    enabled: company.branchCount > 0,
    staleTime: 60_000,
  });

  /*
   * One company at a time, because that is how it is used: somebody is looking
   * at CEF, knows Stockport belongs to it, and says so. The route takes a list
   * and the duplicate card sends one -- there is no reason for a second shape
   * here as well.
   */
  const addBranch = useMutation({
    mutationFn: (branchId: number) =>
      crmApi.groupCompanies({
        companyIds: [branchId],
        parentCompanyId: company.id,
      }),
    onSuccess: () => {
      setAdding(false);
      /* The whole CRM: this company's branches, the branch's own page, and the
         duplicate queue, which withdraws the flags between new siblings. */
      void queryClient.invalidateQueries({ queryKey: ["crm"] });
    },
  });

  if (company.parent !== null) return null;

  const data = rollup.data?.rollup;
  const isGroup = company.branchCount > 0;

  return (
    <section className="rounded-lg border border-line">
      <header
        className={cn(
          "flex items-center gap-2 px-3 py-2",
          (isGroup || adding) && "border-b border-line",
        )}
      >
        <h2 className="text-xs font-semibold uppercase tracking-wide text-faint">
          Group
        </h2>
        {/*
          Through to the ordinary companies list, filtered by parent. It already
          has search, paging, sorting and every column -- a second directory
          would be a worse copy of it that somebody has to keep in step.
        */}
        {isGroup ? (
          <Link
            to={`/crm/companies?parent=${company.id}`}
            className="ml-auto text-xs text-brand-600 hover:text-brand-700"
          >
            All {company.branchCount} branches
          </Link>
        ) : (
          <span className="ml-auto text-[11px] text-faint">No branches</span>
        )}
        <Button
          size="sm"
          variant={adding ? "primary" : "ghost"}
          onClick={() => setAdding(!adding)}
        >
          {adding ? "Cancel" : "Add branch"}
        </Button>
      </header>

      {adding ? (
        <div className="border-b border-line px-3 py-2">
          {/* A company that is already a group may be offered: joining absorbs,
              so its branches come up a level and end up beside it here rather
              than two levels down -- see `planAbsorption`. What is excluded is
              a company already inside another group, which is the one thing
              the two-level rule cannot allow. */}
          <CompanyPicker
            placeholder="Which company is a branch of this group?"
            exclude={[company.id]}
            allow={(candidate) => candidate.parentCompanyId === null}
            onPick={(candidate) => addBranch.mutate(candidate.id)}
          />
          <p className="mt-1 text-[11px] text-faint">
            {addBranch.isPending
              ? "Filing…"
              : "It keeps its own people, tickets, orders and rep. Only the group above it changes — and if it has branches of its own they come with it, as branches of this group."}
          </p>
          {addBranch.isError ? (
            <p className="mt-1 text-[11px] text-sla-breached">
              {addBranch.error instanceof ApiError
                ? addBranch.error.message
                : "That did not work. Try again."}
            </p>
          ) : null}
        </div>
      ) : null}

      {!isGroup ? null : (
      <div className="grid grid-cols-3 divide-x divide-line border-b border-line">
        <Stat label="Branches" value={company.branchCount} />
        <Stat label="People" value={data?.contactCount} />
        <Stat label="Open tickets" value={data?.openTicketCount} />
      </div>
      )}

      {data && data.owners.length > 0 ? (
        <div className="border-b border-line px-3 py-2">
          <p className="mb-1 text-[11px] uppercase tracking-wide text-faint">
            Managed by
          </p>
          <ul className="flex flex-wrap gap-1">
            {data.owners.map((owner) => (
              <li
                key={owner.userId ?? "unowned"}
                className="rounded-full bg-surface-2 px-2 py-0.5 text-[11px] text-muted-on-tint"
              >
                {owner.name}{" "}
                <span className="tabular-nums text-faint">{owner.branches}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {data && data.regions.length > 0 ? (
        <div className="border-b border-line px-3 py-2">
          <p className="mb-1 text-[11px] uppercase tracking-wide text-faint">
            Regions
          </p>
          <ul className="flex flex-wrap gap-1">
            {data.regions.map((region) => (
              <li key={region.region}>
                <Link
                  to={`/crm/companies?parent=${company.id}`}
                  className="rounded-full bg-surface-2 px-2 py-0.5 text-[11px] text-muted-on-tint hover:text-brand-600 dark:hover:text-brand-400"
                >
                  {region.region}{" "}
                  <span className="tabular-nums text-faint">
                    {region.branches}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <ul className="divide-y divide-line">
        {company.branches.map((branch) => (
          <li key={branch.id} className="px-3 py-1.5 text-xs">
            <Link
              to={`/crm/companies/${branch.id}`}
              className="text-content hover:text-brand-600"
            >
              {branch.name}
            </Link>
            <div className="text-[11px] text-faint">
              {[branch.city, branch.tradeAccountNumber, branch.ownerName]
                .filter(Boolean)
                .join(" · ") || "No details"}
            </div>
          </li>
        ))}
      </ul>

      {company.branchCount > company.branches.length ? (
        <Link
          to={`/crm/companies?parent=${company.id}`}
          className="block border-t border-line px-3 py-1.5 text-[11px] text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          …and {company.branchCount - company.branches.length} more
        </Link>
      ) : null}
    </section>
  );
}

function Stat({ label, value }: { label: string; value?: number }) {
  return (
    <div className="px-3 py-2">
      <div className="text-sm font-semibold tabular-nums text-content">
        {value === undefined ? "—" : value.toLocaleString()}
      </div>
      <div className="text-[11px] text-faint">{label}</div>
    </div>
  );
}
